import { useState, useEffect } from 'react'
import { apiGet, apiPost, apiPut } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import {
  Plus,
  Edit2,
  Target,
  FileText,
  Camera,
  Timer,
  Shield,
  Users,
  ClipboardCheck,
  Calendar,
  FileSpreadsheet,
  Package,
  CalendarClock,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Bell,
  X,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

// Submission flow types
const SUBMISSION_FLOWS = [
  {
    id: 'document_upload',
    name: 'Document Upload',
    description: 'Simple document upload with optional notes',
    icon: FileText,
    features: ['documents']
  },
  {
    id: 'multi_document',
    name: 'Multi-Document Upload',
    description: 'Upload multiple documents (up to max limit)',
    icon: ClipboardCheck,
    features: ['documents', 'multi_upload']
  },
  {
    id: 'photo_timer',
    name: 'Photo with Timer',
    description: 'Start/stop timer with photo capture at each point',
    icon: Timer,
    features: ['photos', 'timer']
  },
  {
    id: 'photo_timer_ai',
    name: 'Photo Timer + AI Verification',
    description: 'Timer with AI-verified photo capture (e.g., briefing verification)',
    icon: Shield,
    features: ['photos', 'timer', 'ai_verification']
  },
  {
    id: 'training',
    name: 'Training Record',
    description: 'Timer + multiple photos + optional documents',
    icon: GraduationCap,
    features: ['photos', 'documents', 'timer', 'multi_upload']
  },
]

// Theme colors for PWA display
const THEME_COLORS = [
  { id: 'blue', name: 'Blue', class: 'bg-blue-500' },
  { id: 'green', name: 'Green', class: 'bg-green-500' },
  { id: 'purple', name: 'Purple', class: 'bg-purple-500' },
  { id: 'orange', name: 'Orange', class: 'bg-orange-500' },
  { id: 'pink', name: 'Pink', class: 'bg-pink-500' },
  { id: 'cyan', name: 'Cyan', class: 'bg-cyan-500' },
  { id: 'amber', name: 'Amber', class: 'bg-amber-500' },
  { id: 'violet', name: 'Violet', class: 'bg-violet-500' },
  { id: 'red', name: 'Red', class: 'bg-red-500' },
  { id: 'teal', name: 'Teal', class: 'bg-teal-500' },
]

// Icons for indicators
const INDICATOR_ICONS = [
  { id: 'users', name: 'Users/Meeting', icon: Users },
  { id: 'clipboard', name: 'Clipboard/Checklist', icon: ClipboardCheck },
  { id: 'calendar', name: 'Calendar/Schedule', icon: Calendar },
  { id: 'file', name: 'File/Document', icon: FileText },
  { id: 'spreadsheet', name: 'Spreadsheet/Report', icon: FileSpreadsheet },
  { id: 'package', name: 'Package/Asset', icon: Package },
  { id: 'clock', name: 'Clock/Schedule', icon: CalendarClock },
  { id: 'graduation', name: 'Graduation/Training', icon: GraduationCap },
  { id: 'camera', name: 'Camera/Photo', icon: Camera },
  { id: 'target', name: 'Target/Goal', icon: Target },
]

interface Indicator {
  id: number
  code: string
  name: string
  description: string
  target: number
  points_per_unit: number
  max_points: number
  unit: string
  is_active: boolean
  display_order?: number
  submission_flow?: string
  theme_color?: string
  icon_type?: string
  max_photos?: number
  max_documents?: number
  requires_timer?: boolean
  requires_ai_verification?: boolean
  requires_location?: boolean
  reminder_enabled?: boolean
  reminder_type?: 'deadline' | 'daily'
  reminder_days?: number[]
  deadline_type?: 'period' | 'day_of_month'
  deadline_day?: number
  max_daily_submissions?: number
}

interface FormData {
  code: string
  name: string
  description: string
  target: number
  points_per_unit: number
  max_points: number
  unit: string
  is_active: boolean
  display_order: number
  submission_flow: string
  theme_color: string
  icon_type: string
  max_photos: number
  max_documents: number
  requires_timer: boolean
  requires_ai_verification: boolean
  requires_location: boolean
  reminder_enabled: boolean
  reminder_type: 'deadline' | 'daily'
  reminder_days: number[]
  deadline_type: 'period' | 'day_of_month'
  deadline_day: number | null
  max_daily_submissions: number
}

const defaultFormData: FormData = {
  code: '',
  name: '',
  description: '',
  target: 1,
  points_per_unit: 1,
  max_points: 10,
  unit: 'submissions',
  is_active: true,
  display_order: 0,
  submission_flow: 'document_upload',
  theme_color: 'blue',
  icon_type: 'target',
  max_photos: 0,
  max_documents: 10,
  requires_timer: false,
  requires_ai_verification: false,
  requires_location: true,
  reminder_enabled: true,
  reminder_type: 'deadline',
  reminder_days: [7, 3, 1],
  deadline_type: 'period',
  deadline_day: null,
  max_daily_submissions: 1,
}

export default function IndicatorsPage() {
  const { user, isImpersonating } = useAuthStore()
  const { can } = usePermissions()

  // Only superadmin or impersonating users can add new indicators and reorder
  const canAddAndReorder = user?.role === 'superadmin' || isImpersonating
  // Granular permission checks from role permissions config
  const canCreate = canAddAndReorder || can('admin_indicators', 'create')
  const canEdit = canAddAndReorder || can('admin_indicators', 'edit')

  const [indicators, setIndicators] = useState<Indicator[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIndicator, setEditingIndicator] = useState<Indicator | null>(null)
  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [newReminderDay, setNewReminderDay] = useState('')

  useEffect(() => {
    fetchIndicators()
  }, [])

  const fetchIndicators = async () => {
    try {
      const data = await apiGet<Indicator[]>('/kpi/indicators')
      // Sort by display_order
      const sorted = data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      setIndicators(sorted)
    } catch (error) {
      console.error('Failed to load indicators:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFlowChange = (flowId: string) => {
    const flow = SUBMISSION_FLOWS.find(f => f.id === flowId)
    if (!flow) return

    const updates: Partial<FormData> = { submission_flow: flowId }

    // Auto-configure based on flow
    if (flow.features.includes('timer')) {
      updates.requires_timer = true
    } else {
      updates.requires_timer = false
    }

    if (flow.features.includes('ai_verification')) {
      updates.requires_ai_verification = true
    } else {
      updates.requires_ai_verification = false
    }

    if (flow.features.includes('photos')) {
      updates.max_photos = flowId === 'training' ? 15 : 2
    } else {
      updates.max_photos = 0
    }

    if (flow.features.includes('documents')) {
      updates.max_documents = 10
    } else {
      updates.max_documents = 0
    }

    setFormData({ ...formData, ...updates })
  }

  const addReminderDay = () => {
    const day = parseInt(newReminderDay)
    if (!isNaN(day) && day >= 0 && day <= 30 && !formData.reminder_days.includes(day)) {
      setFormData(prev => ({
        ...prev,
        reminder_days: [...prev.reminder_days, day].sort((a, b) => b - a)
      }))
      setNewReminderDay('')
    }
  }

  const removeReminderDay = (day: number) => {
    setFormData(prev => ({
      ...prev,
      reminder_days: prev.reminder_days.filter(d => d !== day)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingIndicator) {
        await apiPut(`/kpi/indicators/${editingIndicator.id}`, formData)
      } else {
        await apiPost('/kpi/indicators', formData)
      }
      setIsModalOpen(false)
      setEditingIndicator(null)
      setFormData(defaultFormData)
      fetchIndicators()
    } catch (error) {
      console.error('Failed to save indicator:', error)
    }
  }

  const openEditModal = (indicator: Indicator) => {
    setEditingIndicator(indicator)
    setFormData({
      code: indicator.code,
      name: indicator.name,
      description: indicator.description || '',
      target: indicator.target,
      points_per_unit: indicator.points_per_unit,
      max_points: indicator.max_points,
      unit: indicator.unit || 'submissions',
      is_active: indicator.is_active,
      display_order: indicator.display_order || 0,
      submission_flow: indicator.submission_flow || 'document_upload',
      theme_color: indicator.theme_color || 'blue',
      icon_type: indicator.icon_type || 'target',
      max_photos: indicator.max_photos || 0,
      max_documents: indicator.max_documents || 10,
      requires_timer: indicator.requires_timer || false,
      requires_ai_verification: indicator.requires_ai_verification || false,
      requires_location: indicator.requires_location !== false,
      reminder_enabled: indicator.reminder_enabled ?? true,
      reminder_type: indicator.reminder_type || 'deadline',
      reminder_days: indicator.reminder_days || [7, 3, 1],
      deadline_type: indicator.deadline_type || 'period',
      deadline_day: indicator.deadline_day || null,
      max_daily_submissions: indicator.max_daily_submissions ?? 1,
    })
    setNewReminderDay('')
    setIsModalOpen(true)
  }

  const openNewModal = () => {
    setEditingIndicator(null)
    setFormData({
      ...defaultFormData,
      display_order: indicators.length + 1,
    })
    setShowAdvanced(false)
    setIsModalOpen(true)
  }

  const moveIndicator = async (indicator: Indicator, direction: 'up' | 'down') => {
    const currentIndex = indicators.findIndex(i => i.id === indicator.id)
    if (direction === 'up' && currentIndex === 0) return
    if (direction === 'down' && currentIndex === indicators.length - 1) return

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    const newIndicators = [...indicators]
    const [removed] = newIndicators.splice(currentIndex, 1)
    newIndicators.splice(newIndex, 0, removed)

    // Update display orders
    const updates = newIndicators.map((ind, idx) => ({
      id: ind.id,
      display_order: idx + 1,
    }))

    // Update locally first for instant feedback
    setIndicators(newIndicators.map((ind, idx) => ({ ...ind, display_order: idx + 1 })))

    // Update on server
    try {
      for (const update of updates) {
        await apiPut(`/kpi/indicators/${update.id}`, { display_order: update.display_order })
      }
    } catch (error) {
      console.error('Failed to update order:', error)
      fetchIndicators() // Refresh on error
    }
  }

  const toggleActive = async (indicator: Indicator) => {
    try {
      await apiPut(`/kpi/indicators/${indicator.id}`, { is_active: !indicator.is_active })
      fetchIndicators()
    } catch (error) {
      console.error('Failed to toggle status:', error)
    }
  }

  const getFlowInfo = (flowId?: string) => {
    return SUBMISSION_FLOWS.find(f => f.id === flowId) || SUBMISSION_FLOWS[0]
  }

  const getThemeClass = (colorId?: string) => {
    return THEME_COLORS.find(c => c.id === colorId)?.class || 'bg-blue-500'
  }

  const getIconComponent = (iconId?: string) => {
    return INDICATOR_ICONS.find(i => i.id === iconId)?.icon || Target
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Indicators</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure indicators and their display order in the PWA app
          </p>
        </div>
        {canCreate && (
          <button onClick={openNewModal} className="btn-primary">
            <Plus className="h-5 w-5 mr-2" /> Add Indicator
          </button>
        )}
      </div>

      {/* Indicators List */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {canAddAndReorder && (
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 w-12">Order</th>
                )}
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Indicator</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Flow Type</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Target</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Max Pts</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((indicator, index) => {
                const flow = getFlowInfo(indicator.submission_flow)
                const IconComponent = getIconComponent(indicator.icon_type)
                const FlowIcon = flow.icon

                return (
                  <tr key={indicator.id} className="border-b hover:bg-gray-50">
                    {canAddAndReorder && (
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveIndicator(indicator, 'up')}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <span className="text-center text-sm font-medium text-gray-500">
                            {indicator.display_order || index + 1}
                          </span>
                          <button
                            onClick={() => moveIndicator(indicator, 'down')}
                            disabled={index === indicators.length - 1}
                            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getThemeClass(indicator.theme_color)}`}>
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-primary-600">{indicator.code}</span>
                            <span className="font-medium">{indicator.name}</span>
                          </div>
                          {indicator.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">{indicator.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FlowIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{flow.name}</span>
                      </div>
                      <div className="flex gap-1 mt-1">
                        {indicator.requires_timer && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Timer</span>
                        )}
                        {indicator.requires_ai_verification && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">AI</span>
                        )}
                        {(indicator.max_photos || 0) > 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            {indicator.max_photos} Photos
                          </span>
                        )}
                        {(indicator.max_documents || 0) > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                            {indicator.max_documents} Docs
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">{indicator.target}</td>
                    <td className="py-3 px-4 text-center font-semibold">{indicator.max_points}</td>
                    <td className="py-3 px-4 text-center">
                      {canEdit ? (
                        <button
                          onClick={() => toggleActive(indicator)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                            indicator.is_active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {indicator.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          {indicator.is_active ? 'Active' : 'Hidden'}
                        </button>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                          indicator.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {indicator.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          {indicator.is_active ? 'Active' : 'Hidden'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {canEdit && (
                        <button
                          onClick={() => openEditModal(indicator)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <h2 className="text-xl font-bold">
                {editingIndicator ? 'Edit Indicator' : 'New Indicator'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5" /> Basic Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="C.1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Periodic meeting with customer"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this indicator..."
                  />
                </div>
              </div>

              {/* Submission Flow */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Submission Flow
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  {SUBMISSION_FLOWS.map((flow) => {
                    const FlowIcon = flow.icon
                    const isSelected = formData.submission_flow === flow.id

                    return (
                      <label
                        key={flow.id}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="submission_flow"
                          value={flow.id}
                          checked={isSelected}
                          onChange={() => handleFlowChange(flow.id)}
                          className="mt-1"
                        />
                        <FlowIcon className={`h-5 w-5 mt-0.5 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                        <div className="flex-1">
                          <p className={`font-medium ${isSelected ? 'text-primary-700' : 'text-gray-900'}`}>
                            {flow.name}
                          </p>
                          <p className="text-sm text-gray-500">{flow.description}</p>
                          <div className="flex gap-1 mt-1">
                            {flow.features.map(f => (
                              <span key={f} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {f.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Appearance */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" /> PWA Appearance
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Theme Color</label>
                    <div className="grid grid-cols-5 gap-2">
                      {THEME_COLORS.map((color) => (
                        <button
                          key={color.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, theme_color: color.id })}
                          className={`w-8 h-8 rounded-lg ${color.class} ${
                            formData.theme_color === color.id ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                          }`}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                    <div className="grid grid-cols-5 gap-2">
                      {INDICATOR_ICONS.map((iconItem) => {
                        const IconComp = iconItem.icon
                        return (
                          <button
                            key={iconItem.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon_type: iconItem.id })}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
                              formData.icon_type === iconItem.id
                                ? 'border-primary-500 bg-primary-50 text-primary-600'
                                : 'border-gray-200 text-gray-400 hover:bg-gray-50'
                            }`}
                            title={iconItem.name}
                          >
                            <IconComp className="h-4 w-4" />
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scoring */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5" /> Scoring Configuration
                </h3>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.target}
                      onChange={(e) => setFormData({ ...formData, target: parseFloat(e.target.value) })}
                      min="1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Points/Unit</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      value={formData.points_per_unit}
                      onChange={(e) => setFormData({ ...formData, points_per_unit: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Points</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      value={formData.max_points}
                      onChange={(e) => setFormData({ ...formData, max_points: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Label</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="submissions"
                    />
                  </div>
                </div>

                {/* Max Daily Submissions */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Submissions per Day
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      className="input w-24"
                      value={formData.max_daily_submissions}
                      onChange={(e) => setFormData({ ...formData, max_daily_submissions: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                    <span className="text-sm text-gray-500">
                      {formData.max_daily_submissions === 0 ? '(Unlimited)' : 'per day'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Set to 0 for unlimited submissions per day
                  </p>
                </div>
              </div>

              {/* Deadline Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" /> Deadline Settings
                </h3>

                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Deadline Type
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.deadline_type !== 'day_of_month'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="radio"
                          name="deadline_type"
                          value="period"
                          checked={formData.deadline_type !== 'day_of_month'}
                          onChange={() => setFormData(prev => ({ ...prev, deadline_type: 'period', deadline_day: null }))}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Use Period Deadline</p>
                          <p className="text-xs text-gray-500">Same deadline as the KPI period (end of month)</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.deadline_type === 'day_of_month'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="radio"
                          name="deadline_type"
                          value="day_of_month"
                          checked={formData.deadline_type === 'day_of_month'}
                          onChange={() => setFormData(prev => ({ ...prev, deadline_type: 'day_of_month', deadline_day: prev.deadline_day || 5 }))}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Custom Day of Month</p>
                          <p className="text-xs text-gray-500">Set a specific day each month</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {formData.deadline_type === 'day_of_month' && (
                    <div className="border-t pt-4">
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Deadline Day (of the following month)
                      </label>
                      <div className="flex items-center gap-3">
                        <select
                          value={formData.deadline_day || 5}
                          onChange={(e) => setFormData(prev => ({ ...prev, deadline_day: parseInt(e.target.value) }))}
                          className="input w-32"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>
                              {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}
                            </option>
                          ))}
                        </select>
                        <span className="text-sm text-gray-500">of next month</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Example: If period is January, deadline will be on the {formData.deadline_day || 5}th of February
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reminder Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Bell className="h-5 w-5" /> Reminder Notifications
                </h3>

                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Enable Reminders</p>
                      <p className="text-xs text-gray-500">Send reminders to supervisors for this KPI</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, reminder_enabled: !prev.reminder_enabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.reminder_enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.reminder_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {formData.reminder_enabled && (
                    <>
                      {/* Reminder Type Selection */}
                      <div className="border-t pt-4">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Reminder Type
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              formData.reminder_type === 'deadline'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name="reminder_type"
                              value="deadline"
                              checked={formData.reminder_type === 'deadline'}
                              onChange={() => setFormData(prev => ({ ...prev, reminder_type: 'deadline' }))}
                              className="mt-1"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Before Deadline</p>
                              <p className="text-xs text-gray-500">Send X days before period deadline</p>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              formData.reminder_type === 'daily'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name="reminder_type"
                              value="daily"
                              checked={formData.reminder_type === 'daily'}
                              onChange={() => setFormData(prev => ({ ...prev, reminder_type: 'daily' }))}
                              className="mt-1"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Daily</p>
                              <p className="text-xs text-gray-500">Send every day during active period</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Deadline-specific settings */}
                      {formData.reminder_type === 'deadline' && (
                        <div className="border-t pt-4">
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Send reminders X days before deadline:
                          </label>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {formData.reminder_days.map(day => (
                              <span
                                key={day}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                              >
                                {day === 0 ? 'On day' : day === 1 ? '1 day' : `${day} days`}
                                <button
                                  type="button"
                                  onClick={() => removeReminderDay(day)}
                                  className="hover:text-blue-900"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <select
                              value={newReminderDay}
                              onChange={(e) => setNewReminderDay(e.target.value)}
                              className="input text-sm py-1.5 flex-1"
                            >
                              <option value="">Add reminder day...</option>
                              {[0, 1, 2, 3, 5, 7, 10, 14, 21, 30]
                                .filter(d => !formData.reminder_days.includes(d))
                                .map(d => (
                                  <option key={d} value={d}>
                                    {d === 0 ? 'On deadline day' : d === 1 ? '1 day before' : `${d} days before`}
                                  </option>
                                ))}
                            </select>
                            <button
                              type="button"
                              onClick={addReminderDay}
                              disabled={!newReminderDay}
                              className="btn-secondary text-sm py-1.5 px-3 disabled:opacity-50"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Daily reminder info */}
                      {formData.reminder_type === 'daily' && (
                        <div className="border-t pt-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                              Daily reminders will be sent every day during the active period at the company's configured reminder time.
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Supervisors who have already submitted for the day will not receive reminders.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Advanced Settings
                </button>

                {showAdvanced && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Photos</label>
                        <input
                          type="number"
                          className="input"
                          value={formData.max_photos}
                          onChange={(e) => setFormData({ ...formData, max_photos: parseInt(e.target.value) })}
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = no photo capture</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Documents</label>
                        <input
                          type="number"
                          className="input"
                          value={formData.max_documents}
                          onChange={(e) => setFormData({ ...formData, max_documents: parseInt(e.target.value) })}
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = no document upload</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.requires_timer}
                          onChange={(e) => setFormData({ ...formData, requires_timer: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Requires Timer (start/stop tracking)</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.requires_ai_verification}
                          onChange={(e) => setFormData({ ...formData, requires_ai_verification: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Requires AI Photo Verification</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.requires_location}
                          onChange={(e) => setFormData({ ...formData, requires_location: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Capture Location on Submit</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Active (visible in PWA app)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingIndicator ? 'Update Indicator' : 'Create Indicator'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
